import { Account, Entry } from '../types/cit-public-api.d';
import { isValidHexColor } from './colorUtils';
import { DEFAULT_BACKGROUND_COLOR, IFRAME_BACKGROUND_COLOR } from './constants';
import { getPrimaryMedium, hasPrimaryMedium } from './media-utils';
import { getProperty, hasNamePath } from './properties';

interface ThumbnailSlotCustomization {
  slot1?: string[];
  slot2?: string[];
  slot3?: string[];
}

export function getThumbnailSlotValues(account: Account | undefined, entry: Entry) {
  const { properties } = entry;

  const thumbnail = account?.customization?.thumbnail as ThumbnailSlotCustomization | undefined;

  const slot1Path = thumbnail && thumbnail.slot1;
  const slot1 =
    (properties &&
      (slot1Path ? getProperty(properties, slot1Path) : getProperty(properties, hasNamePath))) ||
    '';

  const slot2Path = thumbnail && thumbnail.slot2;
  const slot2 = slot2Path
    ? (properties && getProperty(properties, slot2Path)) || ''
    : entry.classification;

  const slot3Path = thumbnail && thumbnail.slot3;
  const slot3 = (slot3Path && properties && getProperty(properties, slot3Path)) || '';

  return [slot1, slot2, slot3];
}

/**
 * Get the background color for the account or entry media
 * @param account - The account to get the background color for
 * @param citOrigin - The origin of the request
 * @param media - The entry media to get the background color for
 * @param useAccountBackground - Whether to use the account background color, specifically for account home pages and folder pages
 * @returns The background color for the account or entry media
 */
export function getBackgroundColor(
  account?: Account,
  citOrigin?: string,
  entry?: Entry,
  useAccountBackground?: boolean
): string {
  let bgColor: string;

  if (citOrigin === 'iframe') {
    // if in iframe return iframe background color and disregard all other variables
    return `#${IFRAME_BACKGROUND_COLOR}`;
  } else if (account?.theme?.backgroundColor) {
    bgColor = isValidHexColor(account.theme.backgroundColor)
      ? `#${account.theme.backgroundColor}`
      : `#${DEFAULT_BACKGROUND_COLOR}`;
  } else if (useAccountBackground && account?.background?.derivatives?.dominant_color) {
    bgColor = `#${account.background.derivatives.dominant_color}`;
  } else if (
    !useAccountBackground &&
    hasPrimaryMedium(entry?.media) &&
    getPrimaryMedium(entry?.media)?.derivatives?.dominant_color
  ) {
    const primaryMedium = getPrimaryMedium(entry?.media);

    if (primaryMedium?.derivatives?.dominant_color) {
      bgColor = `#${primaryMedium.derivatives.dominant_color}`;
    } else {
      bgColor = `#${DEFAULT_BACKGROUND_COLOR}`;
    }
  } else {
    bgColor = `#${DEFAULT_BACKGROUND_COLOR}`;
  }

  return bgColor;
}
